<template>
  <div class="deductions-form">
    <!-- User Information Box -->
    <box
      type="primary"
      :title="t('Colaborador')"
      elem-id="boxUserInfo">
      <div class="user-info-content">
        <i class="pi pi-user"></i>
        <span class="user-name">{{ userName }}</span>
      </div>
    </box>

    <!-- Deduction Details Box -->
    <box
      type="primary"
      :title="t('Informações da Dedução')"
      elem-id="boxDeductionDetails">

      <div class="form-group">
        <input-currency
          data-testid="deductions-amount-input"
          elem-id="deduction_amount"
          :label="t('Valor da Dedução')"
          :validation="() => isAmountValid"
          :required="true"
          v-model="formData.amount">
        </input-currency>
      </div>

      <div class="form-group">
        <input-rich-text
          data-testid="deductions-description-input"
          elem-id="deductionDescription"
          :label="t('Descrição')"
          :placeholder="t('Ex: Adiantamento salarial, Reembolso de dano, Empréstimo pessoal')"
          :validation="() => isDescriptionValid"
          :required="true"
          :show-toolbar="false"
          v-model="formData.description">
        </input-rich-text>
      </div>

      <div class="form-group">
        <input-number
          data-testid="deductions-despesa-id-input"
          elem-id="deduction_despesa_id"
          :label="t('Despesa Vinculada (Opcional)')"
          :placeholder="t('ID da despesa de origem')"
          :validation="() => true"
          :use-grouping="false"
          v-model="formData.despesa_id">
        </input-number>
        <small class="p-help">
          {{ t('Se esta dedução está relacionada a uma despesa específica, informe o ID') }}
        </small>
      </div>
    </box>

    <!-- Form Actions -->
    <div v-if="!hideActions" class="form-actions">
      <Button
        type="button"
        data-testid="deductions-cancel-button"
        :label="t('Cancelar')"
        icon="pi pi-times"
        class="p-button-text"
        @click="handleClose"
      />
      <Button
        type="submit"
        data-testid="deductions-submit-button"
        :label="t('Adicionar Dedução')"
        icon="pi pi-check"
        :disabled="!canSave || isLoading"
        :loading="isLoading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserDeductions } from '@/composables/useUserDeductions';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface DeductionFormData {
  amount: number;
  description: string;
  despesa_id: number | null;
}

const props = defineProps<{
  periodId: number;
  userId: string;
  userName: string;
  franchiseId: number;
  hideActions?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved'): void;
}>();

const { t } = useI18n();
const { addDeduction, isLoading } = useUserDeductions();

const formData = ref<DeductionFormData>({
  amount: 0,
  description: '',
  despesa_id: null
});

const isAmountValid = computed<boolean>(() => {
  return Boolean(formData.value.amount && formData.value.amount > 0);
});

const isDescriptionValid = computed<boolean>(() => {
  return Boolean(formData.value.description && formData.value.description.trim() !== '');
});

const canSave = computed<boolean>(() => {
  return isAmountValid.value && isDescriptionValid.value;
});

const handleSubmit = async (): Promise<void> => {
  log('DeductionsForm', 'handleSubmit', 'Submitting user deduction', {
    periodId: props.periodId,
    userId: props.userId,
    amount: formData.value.amount,
    description: formData.value.description,
    despesa_id: formData.value.despesa_id
  });

  try {
    const deductionData = {
      period_id: props.periodId,
      user_id: props.userId,
      amount: formData.value.amount,
      description: formData.value.description,
      despesa_id: formData.value.despesa_id ?? undefined,
      franchise_id: props.franchiseId
    };

    await addDeduction(deductionData);

    eventBus.emit('deduction.created', {
      periodId: props.periodId,
      userId: props.userId
    });

    eventBus.emit('globalMessage.success', {
      message: t('Dedução adicionada com sucesso'),
      duration: 3000
    });

    emit('saved');
  } catch (error) {
    const message = error instanceof Error ? error.message : t('Falha ao adicionar dedução');
    console.error('Error adding deduction:', error);
    eventBus.emit('globalMessage.error', {
      message,
      duration: 5000
    });
  }
};

const handleClose = (): void => {
  emit('close');
};

const resetForm = (): void => {
  formData.value = {
    amount: 0,
    description: '',
    despesa_id: null
  };
};

defineExpose({
  handleSubmit,
  canSave,
  resetForm
});
</script>

<style scoped>
.deductions-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.user-info-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.user-info-content i {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.user-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: var(--text-color);
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.p-help {
  display: block;
  margin-top: 0.5rem;
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--surface-200);
}
</style>
